import { Config, configureChains, createConfig } from "wagmi";
import { publicProvider } from "wagmi/providers/public";
import { jsonRpcProvider } from "wagmi/providers/jsonRpc";
import { Env } from "../config/env";
import * as Sentry from "@sentry/browser";
import { Chain, connectorsForWallets } from "@rainbow-me/rainbowkit";
import {
  coinbaseWallet,
  injectedWallet,
  metaMaskWallet,
  phantomWallet,
  rainbowWallet,
  walletConnectWallet,
  zerionWallet,
} from "@rainbow-me/rainbowkit/wallets";

import { arbitrum, mainnet, polygon } from "wagmi/chains";
import { MockConnector } from "@wagmi/connectors/mock";
import { PROJECT_NAME } from "config/consts";
import { exodusWallet } from "../config/connectors/exodusWallet/exodusWallet";

export const NETWORK_TO_NODE_MAP: Record<string, string> = {
  [arbitrum.name]: "arbitrum", // Arbitrum has a trailing slash whereas other chain endpoints do not.
  [mainnet.name]: "eth",
  [polygon.name]: "polygon",
};

export const NETWORK_TO_PRICE_ENDPOINT_MAP: Record<string, string> = {
  [arbitrum.name]: "arbitrum", // Arbitrum has a trailing slash whereas other chain endpoints do not.
  [mainnet.name]: "ethereum",
  [polygon.name]: "polygon",
};

const ENV_KEY = "VITE_TARGET_ENV";
const WALLET_CONNECT_PROJECT_ID = "ce6a900e4631f5d75cd6984f0ed4e383";

const getRpcUrlForEnv = (env: Env, chain: Chain) => {
  const node = NETWORK_TO_NODE_MAP[chain.name];
  return `https://api.bebop.xyz/node/${node}`;
};
export const bootstrapWagmi = () => {
  const { publicClient, chains, webSocketPublicClient } = configureChains<Chain>(
    [mainnet, polygon, arbitrum],
    [
      jsonRpcProvider({
        rpc: chain => {
          const url = getRpcUrlForEnv(import.meta.env[ENV_KEY] as Env, chain);
          return {
            http: url,
          };
        },
      }),
      publicProvider(),
    ],
    { stallTimeout: 5000 }
  );

  const wallets = [
    zerionWallet({
      projectId: WALLET_CONNECT_PROJECT_ID,
      chains,
    }),
    injectedWallet({ chains }),
    metaMaskWallet({ chains, projectId: WALLET_CONNECT_PROJECT_ID }),
    rainbowWallet({ chains, projectId: WALLET_CONNECT_PROJECT_ID }),
    coinbaseWallet({ chains, appName: PROJECT_NAME }),
    walletConnectWallet({ chains, projectId: WALLET_CONNECT_PROJECT_ID }),
    phantomWallet({ chains }),
    exodusWallet({
      chains,
      shimDisconnect: true,
    }),
  ];
  const customConnectors = connectorsForWallets([
    {
      groupName: "Recommended",
      wallets,
    },
  ]);
  const WAGMI_CONFIG = {
    autoConnect: true,
    publicClient,
    webSocketPublicClient,
    logger: {
      warn: (warning: string) => {
        console.warn(warning);
        Sentry.captureException(warning, {
          extra: { source: "wagmi" },
        });
      },
    },
    connectors: customConnectors,
  };
  const reactClient = createConfig(WAGMI_CONFIG);
  return reactClient as Config;
};

export const createMockWagmiConfig = (mockConnector: MockConnector) => {
  const { publicClient } = configureChains<Chain>(
    [mainnet, polygon, arbitrum],
    [
      jsonRpcProvider({
        rpc: chain => {
          const url = getRpcUrlForEnv(import.meta.env[ENV_KEY] as Env, chain);
          return {
            http: url,
          };
        },
      }),
      publicProvider(),
    ],
    { stallTimeout: 5000 }
  );
  return createConfig({
    publicClient,
    connectors: [mockConnector],
  });
};
